import React, { useState, useEffect, useContext, useRef } from 'react'
import { StyleSheet, ScrollView, View, TouchableOpacity, FlatList } from 'react-native'
import { Text, Spinner, RangeDatepicker, Layout, Card, Icon, Popover, Button, useTheme } from '@ui-kitten/components'
import { getSuppliesOnDateRange, getStatsForDays, getComplexSalesOnDateRange, getComplexSaleTransactionsOnDateRange, getMonthlyStatsOnDateRange } from '../../global/Firebase/firebase'
import { recalculateUserSalaryAmounts } from '../../global/Firebase/workingHours'
import { rethrowUnlessWRefusal } from '../../global/Write/writeW'
import { accrueEmployeeSalaries, fetchSalaryAccruals } from './salaryAccruals'
import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import { useColors } from '../../utils/useColors'
import Divider from '../../components/Divider'
import { useTranslation } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose } from '../../utils/timezoneFunctions'
import { toNestedDate } from '../../utils/nestedDateValue'
import { useAtomValue } from 'jotai'
import { constantsAtom, currencyFormatterAtom, inactiveUsersAtom, localeDateServiceAtom, storesDictAtom, suppliersDictAtom, usersDictAtom } from '../../global/AtomsProvider'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { format } from 'date-fns'
import { ModalView } from '../../components/ModalView'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { addDecimalAmounts } from '../../utils/decimalInput'
import { calculateUpcomingPayments } from './upcomingPayments'
import * as Sentry from '@sentry/react-native'

// Helper component for rows with info popover
/**
 * ATL-1371 — the amount the «оплатить» shortcuts prefill into the payment form.
 *
 * Both sides are FLOAT ACCUMULATIONS (`totalDebit` sums supply totals, `totalCreditCurrentPeriod`
 * sums transaction values), so a supplier who is settled to the cent does not necessarily subtract
 * to exactly 0: measured, supplies of 1.1 + 2.2 against a payment of 3.3 leave
 * `4.440892098500626e-16`. That residual is invisible — `currencyFormatter` renders it as "0", and
 * the balance line beside the button says 0 — but it is strictly greater than zero, so it walks
 * straight past the form's "amount must be more than 0" guard and prefills a payment nobody meant
 * to make. `saveNewTransaction` then marks supplies paid on the strength of it.
 *
 * Rounding at the prefill, not with an epsilon in the validator: the threshold below which a
 * payment is meaningless is a currency's business, while a difference that only exists because
 * doubles are binary is arithmetic, and it belongs where the arithmetic happens.
 *
 * This is reachable today — supply totals have always been fractional — and ATL-1371 makes the
 * credit side fractional too, so it gets more likely rather than newly possible.
 *
 * @param {{ totalDebit?: number, totalCreditCurrentPeriod?: number }} value
 * @returns {number|null} null only if `addDecimalAmounts` cannot add — unreachable here, since both
 *   operands are coerced to numbers, but the type is the helper's and not a wish. The consumer
 *   (`PaymentTransactionDetailScreen`, `newPaymentDetail?.value?.toString() || ''`) renders it as
 *   an empty box, which is the honest rendering of a balance nobody can compute.
 */
export const prefilledBalance = (value) =>
  addDecimalAmounts(value?.totalDebit || 0, -(value?.totalCreditCurrentPeriod || 0))

const InfoRow = ({ labelKey, value, t, currencyFormatter, isBold, noFormat }) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const triggerRef = useRef(null);
  const descriptionKey = `${labelKey}_description`;
  const descriptionText = t(descriptionKey, { nsSeparator: '###' }); // Use a non-existent nsSeparator to ensure raw key access if not found
  const hasDescription = descriptionText !== descriptionKey;

  const renderTrigger = () => (
    <TouchableOpacity
      ref={triggerRef}
      onPress={() => setPopoverVisible(true)}
      activeOpacity={0.7}
    >
      <Text category={isBold ? 's1' : 'p1'} style={{ flexDirection: 'row' }}>
        {t(labelKey) + (noFormat ? value : currencyFormatter.format(value))}
        <Icon name='question-mark-circle-outline' width={16} height={16} style={{ marginLeft: 4 }} fill='#888' />
      </Text>
    </TouchableOpacity>
  );

  return (
    <View style={{ marginBottom: 8 }}>
      {hasDescription ? (
        <Popover
          visible={popoverVisible}
          anchor={renderTrigger}
          onBackdropPress={() => setPopoverVisible(false)}
          placement='top start'
        >
          <Layout style={{ padding: 10, minWidth: 180, maxWidth: 300 }}>
            <Text>{descriptionText}</Text>
          </Layout>
        </Popover>
      ) : (
        <Text category={isBold ? 's1' : 'p1'} style={{ flex: 1, flexWrap: 'wrap' }}>
          {t(labelKey)}
          {noFormat ? value : currencyFormatter.format(value)}
        </Text>
      )}
    </View>
  );
};

export default function PaymentStatsScreen({ navigation }) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const usersDict = useAtomValue(usersDictAtom)
  const inactiveUsers = useAtomValue(inactiveUsersAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const constants = useAtomValue(constantsAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const [range, setRange] = useState({})
  const [store, setStore] = useState(context.user.store?.id)
  const [employeeSalaries, setEmployeeSalaries] = useState()
  const [supplierPayments, setSupplierPayments] = useState()
  const [saleAmount, setSaleAmount] = useState()
  const [spendingPayments, setSpendingPayments] = useState()
  const [bankPercent, setBankPercent] = useState()
  const [overallStats, setOverallStats] = useState()
  const [cashboxShiftDifference, setCashboxShiftDifferece] = useState()
  const [monthlySpendingPayments, setMonthlySpendingPayments] = useState()
  const [complexSales, setComplexSales] = useState(null)
  // ATL-1990 — whether the figures on screen were actually computed for the selected period.
  // 'loading' | 'ready' | 'error'. Every figure is hidden unless 'ready': a step that threw used to
  // leave the state unset, and `currencyFormatterAtom` renders unset as «0 ₸» — an owner saw a whole
  // month of zeros, which reads as "no money moved", not as "the report broke".
  const [loadState, setLoadState] = useState('loading')
  // Each load gets a generation; a load that is no longer the latest (another period was picked, the
  // screen was left) must not write state, report, or keep a Firestore listener alive.
  const loadGenerationRef = useRef(0)
  const unsubscribeTransactionsRef = useRef(null)
  const lastRequestRef = useRef(null)
  const _rangePicker = React.createRef()
  const [refreshingAllTimeSalaryPaidCredited, setRefreshingAllTimeSalaryPaidCredited] = useState(false)
  const [selectedUserForRecalculation, setSelectedUserForRecalculation] = useState(null);
  const [foundErrorModalIsShown, setFoundErrorModalIsShown] = useState(false)
  
  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            navigation.navigate('PaymentTransactionDetail', { detail: null })
          }}
          iconName='plus-outline'
        />
      ),
    })
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
    var startOfMonth = new Date()
    startOfMonth.setDate(1)
    startOfMonth.setHours(0,0,0,0)
    
    // Set end date to last day of current month
    let endOfMonth = new Date()
    endOfMonth.setDate(1)  // Set to 1st to avoid overflow when changing month
    endOfMonth.setMonth(endOfMonth.getMonth() + 1)
    endOfMonth.setDate(0)  // Day 0 = last day of previous month
    endOfMonth.setHours(23,59,59,999)
    
    let newRange = {startDate: startOfMonth, endDate: endOfMonth}
    setRange(newRange)
    void getDebitCredit(newRange, store)
    return () => {
      // Leaving the screen ends the current load: nothing may land after unmount.
      loadGenerationRef.current += 1
      stopTransactionListener()
    }
  }, [])

  useEffect(() => {
    if (usersDict) {
      // Calculate allTimeDebit and allTimeCredit from usersDict
      let allTimeDebit = 0;
      let allTimeCredit = 0;
      for (let uid in usersDict) {
        // if user salaryCreditedAmount or salaryPaidAmount is null, recalculate it
        if ((usersDict[uid]?.salaryCreditedAmount == null || usersDict[uid]?.salaryPaidAmount == null)) {
          recalculateUserSalaryAmounts(uid).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
        }
        if ((usersDict[uid]?.store?.id === store || store === 'all') && usersDict[uid]?.salaryPaidAmount != null) {
          allTimeDebit += usersDict[uid].salaryPaidAmount;
        }
        if ((usersDict[uid]?.store?.id === store || store === 'all') && usersDict[uid]?.salaryCreditedAmount != null) {
          allTimeCredit += usersDict[uid].salaryCreditedAmount;
        }
      }
      setEmployeeSalaries(prev => ({...prev, allTimeDebit, allTimeCredit}))
    }
  }, [usersDict])

  /**
   * Main function that fetches and processes all payment data for the selected date range and store
   * Calculates employee salaries, supplier payments, monthly spending, and overall statistics
   * @param {Object} range - Date range with startDate and endDate
   * @param {string} store - Store ID or 'all' for all stores
   */
  // Never rejects: every failure becomes the 'error' load state (ATL-1990), so callers may `void` it.
  const getDebitCredit = async (range, store) => {
    if (range.startDate && range.endDate) {
      lastRequestRef.current = { range: { startDate: range.startDate, endDate: range.endDate }, store }
      const generation = ++loadGenerationRef.current
      const isCurrent = () => generation === loadGenerationRef.current
      stopTransactionListener()
      setLoadState('loading')
      try {
        _rangePicker.current?.blur()
        // ⚠️ `['all']` frames «Все» by the DEVICE's midnight (ATL-1649). Do not switch it to the
        // every-store frame alone: that frame's autoclose-extended end crosses into the next month,
        // and calculateRecurringSpendingAmount would then charge month-end reports two months.
        range = dateRangeWithTimezoneAndAutoclose(range, [store])
        setSupplierPayments(null)
        setEmployeeSalaries(null)
        setComplexSales(null)

        // Fetch all required data
        // ATL-1590: hourly/daily and monthly accruals, each record exactly once, «Все» = every store.
        const salaryAccruals = await fetchSalaryAccruals(range, store)
        const supplies = await getSuppliesOnDateRange(context.db, range, true, store, null, false)
        const unpaidSuppliesPreviousPeriod = await getSuppliesOnDateRange(context.db, 'allTime', true, store, null, true)
        const dailyStats = await getStatsForDays(context.db, range, store)
        const monthlyStats = await getMonthlyStatsOnDateRange(range, store)
        if (!isCurrent()) return

        // Process each data type
        const newEmployeeSalaries = processEmployeeSalaries(salaryAccruals)
        const newSupplierPayments = processSupplierPayments(supplies, unpaidSuppliesPreviousPeriod)
        const newMonthlySpendingPayments = calculateMonthlySpendingPayments(constants.recurringSpendings.data, range, store)
        const { newSaleAmount, newBankPercent, moneyDifferenceAmount } = processDailyStats(dailyStats, constants.paymentMethods.data)

        // Fetch complex sales
        const newComplexSales = await fetchComplexSales(range, store)
        if (!isCurrent()) return
        setComplexSales(newComplexSales)

        // Set up transaction listener — the load is 'ready' once its first snapshot is applied.
        await setupTransactionListener(range, store, newEmployeeSalaries, newSupplierPayments, newMonthlySpendingPayments, newSaleAmount, newBankPercent, moneyDifferenceAmount, newComplexSales, generation)
      } catch (error) {
        failLoad(generation, error, 'load')
      }
    }
  }

  /** ATL-1990 — retry the last requested period/store after a failed load. */
  const retryLoad = () => {
    const last = lastRequestRef.current
    if (last) void getDebitCredit({ ...last.range }, last.store)
  }

  const stopTransactionListener = () => {
    if (unsubscribeTransactionsRef.current) {
      unsubscribeTransactionsRef.current()
      unsubscribeTransactionsRef.current = null
    }
  }

  /**
   * ATL-1990 — a load that failed is shown as failed. Reported (with the step that failed) only
   * when it is still the current load; a superseded load's failure is nobody's report.
   */
  const failLoad = (generation, error, step) => {
    if (generation !== loadGenerationRef.current) return
    stopTransactionListener()
    console.error(`PaymentStats: ${step} failed`, error)
    Sentry.captureException(error, { tags: { screen: 'PaymentStats', payment_stats_step: step } })
    setLoadState('error')
  }

  /**
   * Process employee salaries (hourly, daily, and monthly)
   * The period's accruals come from `accrueEmployeeSalaries` (ATL-1590); this adds the all-time
   * paid/credited totals from usersDict for the selected store.
   * @param {Object} salaryAccruals - `fetchSalaryAccruals` result: dated (hourly/daily) and monthly records
   * @returns {Object} Employee salary data with structure:
   *   {
   *     totalDebit: number - Total amount owed to all employees,
   *     totalCredit: number - Initially 0, populated later with payment data,
   *     allTimeDebit / allTimeCredit: number - All-time salary paid / credited for the store,
   *     [employeeID]: {
   *       totalDebit: number - Amount owed to this employee,
   *       totalCredit: number - Initially 0, populated later with payment data,
   *       workingHours: Array - List of working hour records for this employee,
   *       salaryTransactions: Array - Initially empty, populated later with transactions
   *     }
   *   }
   */
  const processEmployeeSalaries = (salaryAccruals) => {
    let newEmployeeSalaries = accrueEmployeeSalaries(salaryAccruals)
    
    // Calculate allTimeDebit and allTimeCredit from usersDict
    let allTimeDebit = 0;
    let allTimeCredit = 0;
    for (let uid in usersDict) {
      // if user salaryCreditedAmount or salaryPaidAmount is null, recalculate it
      if ((usersDict[uid]?.salaryCreditedAmount == null || usersDict[uid]?.salaryPaidAmount == null)) {
        recalculateUserSalaryAmounts(uid).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
      }
      if ((usersDict[uid]?.store?.id === store || store === 'all') && usersDict[uid]?.salaryPaidAmount != null) {
        allTimeDebit += usersDict[uid].salaryPaidAmount;
      }
      if ((usersDict[uid]?.store?.id === store || store === 'all') && usersDict[uid]?.salaryCreditedAmount != null) {
        allTimeCredit += usersDict[uid].salaryCreditedAmount;
      }
    }
    newEmployeeSalaries.allTimeDebit = allTimeDebit;
    newEmployeeSalaries.allTimeCredit = allTimeCredit;
    
    return newEmployeeSalaries
  }

  /**
   * Process supplier payments
   * Calculates total debits for each supplier from supplies
   * @param {Array} supplies - Supply data
   * @param {Array} unpaidSuppliesPreviousPeriod - Unpaid supplies data from all time
   * @returns {Object} Supplier payment data with structure:
   *   {
   *     totalDebit: number - Total amount owed to all suppliers,
   *     totalCredit: number - Initially 0, populated later with payment data,
   *     [supplierID]: {
   *       totalDebit: number - Amount owed to this supplier,
   *       totalCredit: number - Initially 0, populated later with payment data,
   *       supplies: Array - List of supply records for this supplier,
   *       unpaidSupplies: Array - List of unpaid supply records for this supplier,
   *       transactions: Array - Initially empty, populated later with transactions
   *     }
   *   }
   */
  const processSupplierPayments = (supplies, unpaidSuppliesPreviousPeriod = []) => {
    let newSupplierPayments = {totalDebit: 0, totalCredit: 0, totalUnpaidSumPreviousPeriod: 0}
    
    // Process supplies for current period
    for (let s of supplies) {
      var newData = newSupplierPayments[s.supplierID] ? {...newSupplierPayments[s.supplierID]} : {
        totalDebit: 0,
        totalCredit: 0,
        supplies: [],
        unpaidSupplies: [],
        transactions: []
      }
      newData.totalDebit = (newData.totalDebit || 0) + (s.totalSum || 0)
      newData.supplies = [...newData.supplies, s]
      newSupplierPayments[s.supplierID] = newData
      newSupplierPayments.totalDebit = newSupplierPayments.totalDebit + (s.totalSum || 0)
    }
    
    // Process unpaid supplies from all time
    if (unpaidSuppliesPreviousPeriod && unpaidSuppliesPreviousPeriod.length > 0) {
      for (let s of unpaidSuppliesPreviousPeriod) {
        var newData = newSupplierPayments[s.supplierID] ? {...newSupplierPayments[s.supplierID]} : {
          totalDebit: 0,
          totalCredit: 0,
          supplies: [],
          unpaidSupplies: [],
          transactions: []
        }
        // Only add to unpaidSupplies array, don't affect debit totals as they may be outside current period
        newData.unpaidSupplies = [...(newData.unpaidSupplies || []), s]
        // Calculate total unpaid sum for this supplier
        newData.totalUnpaidSumPreviousPeriod = (newData.totalUnpaidSumPreviousPeriod || 0) + (s.totalSum || 0)
        // Add to total unpaid sum across all suppliers
        newSupplierPayments.totalUnpaidSumPreviousPeriod += (s.totalSum || 0)
        newSupplierPayments[s.supplierID] = newData
      }
    }
    
    return newSupplierPayments
  }

  /**
   * Calculate monthly spending payments
   * Uses recurring spending data to calculate pro-rated amounts for the date range
   * @param {Array} recurringSpendings - Recurring spendings data
   * @param {Object} range - Date range with startDate and endDate
   * @param {string} store - Store ID or 'all' for all stores
   * @returns {Object} Monthly spending data with structure:
   *   {
   *     totalDebit: number - Total amount of all recurring spendings for the period,
   *     totalCredit: number - Initially 0, populated later with payment data,
   *     [spendingName]: {
   *       totalDebit: number - Pro-rated amount for this recurring spending,
   *       totalCredit: number - Initially 0, populated later with payment data
   *     }
   *   }
   */
  const calculateMonthlySpendingPayments = (recurringSpendings, range, store) => {
    let newMonthlySpendingPayments = {totalDebit: 0, totalCredit: 0}
    
    for (let rS of recurringSpendings) {
      if (rS.store.id === store || store === 'all') {
        const recurringAmount = calculateRecurringSpendingAmount(rS, range);
        // Keyed by NAME because that is all a payment carries (`externalDestination.name`), and
        // names are not unique: prod has same-named spendings in one store and across stores.
        // Overwriting kept only the last one's accrual on the payee while the category total kept
        // both — and `calculateUpcomingPayments` sums the payees (ATL-1589), so it would drop one.
        const existing = newMonthlySpendingPayments[rS.name]
        newMonthlySpendingPayments[rS.name] = {
          totalDebit: existing ? addDecimalAmounts(existing.totalDebit, recurringAmount) : recurringAmount,
          totalCredit: 0,
        }
        newMonthlySpendingPayments.totalDebit = newMonthlySpendingPayments.totalDebit + recurringAmount
      }
    }
    
    return newMonthlySpendingPayments
  }

  /**
   * Process daily stats to calculate sales, bank fees, and cashbox differences
   * @param {Array} dailyStats - Daily statistics data
   * @param {Array} paymentMethods - Payment methods data
   * @returns {Object} Processed statistics with properties:
   *   {
   *     newSaleAmount: number - Total sales amount for the period,
   *     newBankPercent: number - Total bank fees calculated from payment methods,
   *     moneyDifferenceAmount: number - Total cashbox difference between actual and system values
   *   }
   */
  const processDailyStats = (dailyStats, paymentMethods) => {
    var newSaleAmount = 0
    var newBankPercent = 0
    var moneyDifferenceAmount = 0
    
    for (const [index, value] of dailyStats.entries()) {
      newSaleAmount += (value.saleAmount || 0)
      newBankPercent += (value.bankPercent || 0)
      for (const [idx, val] of paymentMethods.entries()) {
        moneyDifferenceAmount += ((value[val.id + 'EndAmount'] - value[val.id + 'SystemEndAmount']) || 0)
      }
    }
    
    return { newSaleAmount, newBankPercent, moneyDifferenceAmount }
  }

  /**
   * Process a single transaction based on its type
   * Routes the transaction to the appropriate specialized processor
   * @param {Object} t - Transaction data
   * @param {Object} newEmployeeSalaries - Employee salary data
   * @param {Object} newSupplierPayments - Supplier payment data
   * @param {Object} newMonthlySpendingPayments - Monthly spending data
   * @param {Object} range - Date range
   * @param {string} store - Store ID
   * @returns {Object} One of the following based on transaction type:
   *   - For workingHour: { employeeSalaries: Object, totalCredit: number, totalCreditCurrentPeriod: number }
   *   - For supply: { supplierPayments: Object, totalCredit: number, totalCreditCurrentPeriod: number }
   *   - For recurringSpending: { monthlySpendingPayments: Object, totalCredit: number, totalCreditCurrentPeriod: number }
   *   - For other types: { spendingPayments: number }
   */
  const processTransaction = (t, newEmployeeSalaries, newSupplierPayments, newMonthlySpendingPayments, range, store) => {
    let paymentRangeEndDate 
    if (t.paymentRange?.endDate) {
      paymentRangeEndDate = new Date(t.paymentRange?.endDate?.toDate())
      paymentRangeEndDate.setHours(23,59,59,999)
    }
    
    if (t.linkedDocType === 'workingHour') {
      return processWorkingHourTransaction(t, newEmployeeSalaries, range, paymentRangeEndDate)
    } else if (t.linkedDocType === 'supply') {
      return processSupplyTransaction(t, newSupplierPayments, range, paymentRangeEndDate)
    } else if (t.linkedDocType === 'recurringSpending') {
      return processRecurringSpendingTransaction(t, newMonthlySpendingPayments, range, paymentRangeEndDate, store)
    }
    
    // Return other expenses
    return { spendingPayments: t.value || 0 }
  }

  /**
   * Process working hour transaction
   * Calculates credits for employee salaries, handling payment periods
   * The logic considers:
   * - Current period payments (payment range starts in current period)
   * - Overlapping period payments (payment range overlaps with current period)
   * - Future period payments (payment range starts after the current period)
   * - Payments without specified range
   * @param {Object} t - Transaction data
   * @param {Object} newEmployeeSalaries - Employee salary data
   * @param {Object} range - Date range
   * @param {Date} paymentRangeEndDate - End date of payment range
   * @returns {Object} Object containing:
   *   {
   *     employeeSalaries: Object - Updated employee salaries data structure,
   *     totalCredit: number - Total payment amount from this transaction,
   *     totalCreditCurrentPeriod: number - Amount of payment attributed to current period,
   *     totalCreditFuturePeriod: number - Amount of payment attributed to future period
   *   }
   */
  const processWorkingHourTransaction = (t, newEmployeeSalaries, range, paymentRangeEndDate) => {
    var newData = newEmployeeSalaries[t.externalDestination?.uid] ? 
      {...newEmployeeSalaries[t.externalDestination?.uid]} : 
      {
        totalCredit: 0,
        salaryTransactions: [],
        totalDebit: 0,
        workingHours: []
      }
    
    newData.totalCredit = (newData.totalCredit || 0) + (t.value || 0)
    let currentPeriodCredit = 0
    let futurePeriodCredit = 0
    
    if (t.paymentRange && t.paymentRange.startDate.toDate() >= range.startDate && t.paymentRange.startDate.toDate() <= range.endDate) {
      // Payment range starts in current period - count full amount
      currentPeriodCredit = t.value || 0
      newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
    } else if (t.paymentRange && t.paymentRange.startDate.toDate() < range.startDate && paymentRangeEndDate >= range.startDate) {
      // Overlapping period - calculate proportional amount
      const totalDays = (paymentRangeEndDate - t.paymentRange.startDate.toDate()) / (1000 * 60 * 60 * 24)
      const currentPeriodDays = (paymentRangeEndDate - range.startDate) / (1000 * 60 * 60 * 24)
      const proportion = currentPeriodDays / totalDays
      currentPeriodCredit = (t.value || 0) * proportion
      newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
    } else if (t.paymentRange && t.paymentRange.startDate.toDate() > range.endDate) {
      // Future period payment - count full amount as future
      futurePeriodCredit = t.value || 0
      newData.totalCreditFuturePeriod = (newData.totalCreditFuturePeriod || 0) + futurePeriodCredit
    } else if (!t.paymentRange) {
      // No payment range - count full amount if transaction is in current period
      if (t.date.toDate() >= range.startDate && t.date.toDate() <= range.endDate) {
        currentPeriodCredit = t.value || 0
        newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
      } else if (t.date.toDate() > range.endDate) {
        // Future transaction date
        futurePeriodCredit = t.value || 0
        newData.totalCreditFuturePeriod = (newData.totalCreditFuturePeriod || 0) + futurePeriodCredit
      }
    }
    
    newData.salaryTransactions = [...newData.salaryTransactions, t]
    newEmployeeSalaries[t.externalDestination?.uid] = newData
    
    return { 
      employeeSalaries: newEmployeeSalaries,
      totalCredit: t.value || 0,
      totalCreditCurrentPeriod: currentPeriodCredit,
      totalCreditFuturePeriod: futurePeriodCredit
    }
  }

  /**
   * Process supply transaction
   * Calculates credits for supplier payments, handling payment periods
   * The logic considers:
   * - Current period payments (payment range starts in current period)
   * - Overlapping period payments (payment range overlaps with current period)
   * - Future period payments (payment range starts after the current period)
   * - Payments without specified range
   * @param {Object} t - Transaction data
   * @param {Object} newSupplierPayments - Supplier payment data
   * @param {Object} range - Date range
   * @param {Date} paymentRangeEndDate - End date of payment range
   * @returns {Object} Object containing:
   *   {
   *     supplierPayments: Object - Updated supplier payments data structure,
   *     totalCredit: number - Total payment amount from this transaction,
   *     totalCreditCurrentPeriod: number - Amount of payment attributed to current period,
   *     totalCreditFuturePeriod: number - Amount of payment attributed to future period
   *   }
   */
  const processSupplyTransaction = (t, newSupplierPayments, range, paymentRangeEndDate) => {
    var newData = newSupplierPayments[t.externalDestination?.id] ? 
      {...newSupplierPayments[t.externalDestination?.id]} : 
      {
        totalCredit: 0,
        transactions: [],
        totalDebit: 0,
        supplies: []
      }
    
    newData.totalCredit = (newData.totalCredit || 0) + (t.value || 0)
    let currentPeriodCredit = 0
    let futurePeriodCredit = 0
    
    if (t.paymentRange && t.paymentRange.startDate.toDate() >= range.startDate && t.paymentRange.startDate.toDate() <= range.endDate) {
      // Payment range starts in current period - count full amount
      currentPeriodCredit = t.value || 0
      newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
    } else if (t.paymentRange && t.paymentRange.startDate.toDate() < range.startDate && paymentRangeEndDate >= range.startDate) {
      // Overlapping period - calculate proportional amount
      const totalDays = (paymentRangeEndDate - t.paymentRange.startDate.toDate()) / (1000 * 60 * 60 * 24)
      const currentPeriodDays = (paymentRangeEndDate - range.startDate) / (1000 * 60 * 60 * 24)
      const proportion = currentPeriodDays / totalDays
      currentPeriodCredit = (t.value || 0) * proportion
      newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
    } else if (t.paymentRange && t.paymentRange.startDate.toDate() > range.endDate) {
      // Future period payment - count full amount as future
      futurePeriodCredit = t.value || 0
      newData.totalCreditFuturePeriod = (newData.totalCreditFuturePeriod || 0) + futurePeriodCredit
    } else if (!t.paymentRange) {
      // No payment range - count full amount if linked supply is in current period
      for (let s of newData.supplies) {
        if (s.id === t.linkedDocId) {
          if (s.supplyDate.toDate() >= range.startDate && s.supplyDate.toDate() <= range.endDate) {
            currentPeriodCredit = t.value || 0
            newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
            break
          } else if (s.supplyDate.toDate() > range.endDate) {
            futurePeriodCredit = t.value || 0
            newData.totalCreditFuturePeriod = (newData.totalCreditFuturePeriod || 0) + futurePeriodCredit
            break
          }
        }
      }
    }
    
    newData.transactions = [...newData.transactions, t]
    newSupplierPayments[t.externalDestination?.id] = newData
    
    return { 
      supplierPayments: newSupplierPayments,
      totalCredit: t.value || 0,
      totalCreditCurrentPeriod: currentPeriodCredit,
      totalCreditFuturePeriod: futurePeriodCredit
    }
  }

  /**
   * Process recurring spending transaction
   * Calculates credits for monthly recurring payments, handling payment periods
   * The logic considers:
   * - Current period payments (payment range starts in current period)
   * - Overlapping period payments (payment range overlaps with current period)
   * - Future period payments (payment range starts after the current period)
   * - Payments without specified range
   * @param {Object} t - Transaction data
   * @param {Object} newMonthlySpendingPayments - Monthly spending data
   * @param {Object} range - Date range
   * @param {Date} paymentRangeEndDate - End date of payment range
   * @param {string} store - Store ID
   * @returns {Object} Object containing:
   *   {
   *     monthlySpendingPayments: Object - Updated monthly spending data structure,
   *     totalCredit: number - Total payment amount from this transaction,
   *     totalCreditCurrentPeriod: number - Amount of payment attributed to current period,
   *     totalCreditFuturePeriod: number - Amount of payment attributed to future period
   *   }
   */
  const processRecurringSpendingTransaction = (t, newMonthlySpendingPayments, range, paymentRangeEndDate, store) => {
    if (t.store !== store && store !== 'all') {
      return { monthlySpendingPayments: newMonthlySpendingPayments }
    }
    
    var newData = newMonthlySpendingPayments[t.externalDestination?.name] || {}
    newData.totalCredit = (newData.totalCredit || 0) + (t.value || 0)
    let currentPeriodCredit = 0
    let futurePeriodCredit = 0
    
    if (t.paymentRange && t.paymentRange.startDate.toDate() >= range.startDate && t.paymentRange.startDate.toDate() <= range.endDate) {
      // Payment range starts in current period - count full amount
      currentPeriodCredit = t.value || 0
      newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
    } else if (t.paymentRange && t.paymentRange.startDate.toDate() < range.startDate && paymentRangeEndDate >= range.startDate) {
      // Overlapping period - calculate proportional amount
      const totalDays = (paymentRangeEndDate - t.paymentRange.startDate.toDate()) / (1000 * 60 * 60 * 24)
      const currentPeriodDays = (paymentRangeEndDate - range.startDate) / (1000 * 60 * 60 * 24)
      const proportion = currentPeriodDays / totalDays
      currentPeriodCredit = (t.value || 0) * proportion
      newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
    } else if (t.paymentRange && t.paymentRange.startDate.toDate() > range.endDate) {
      // Future period payment - count full amount as future
      futurePeriodCredit = t.value || 0
      newData.totalCreditFuturePeriod = (newData.totalCreditFuturePeriod || 0) + futurePeriodCredit
    } else if (!t.paymentRange) {
      // No payment range - count full amount if transaction is in current period
      if (t.date.toDate() >= range.startDate && t.date.toDate() <= range.endDate) {
        currentPeriodCredit = t.value || 0
        newData.totalCreditCurrentPeriod = (newData.totalCreditCurrentPeriod || 0) + currentPeriodCredit
      } else if (t.date.toDate() > range.endDate) {
        // Future transaction date
        futurePeriodCredit = t.value || 0
        newData.totalCreditFuturePeriod = (newData.totalCreditFuturePeriod || 0) + futurePeriodCredit
      }
    }
    
    newData.transactions = newData.transactions ? [...newData.transactions, t] : [t]
    newMonthlySpendingPayments[t.externalDestination?.name] = newData
    
    return { 
      monthlySpendingPayments: newMonthlySpendingPayments,
      totalCredit: t.value || 0, 
      totalCreditCurrentPeriod: currentPeriodCredit,
      totalCreditFuturePeriod: futurePeriodCredit
    }
  }

  /**
   * Calculate overall statistics from all payment data
   * 
   * Key calculations:
   * - currentPeriodPayments: All payments made in the current period
   * - currentPeriodSalePayments: Sales amount minus non-paid complex sales amount
   * - currentBalance: Sales minus current period payments
   * - upcomingPayments: Sum over every payee of max(0, debit − current-period credit) (payments still to be made)
   * - upcomingAccountsReceivable: Outstanding balances from complex sales
   * - upcomingBalanceCurrentPeriod: Current balance minus upcoming payments
   * - previousPeriodPayments: Credits from previous periods
   * - futurePeriodPayments: Credits for future periods
   * - upcomingBalanceWithPreviousPeriodPayments: Final balance considering all factors
   * 
   * @param {Object} newEmployeeSalaries - Employee salary data
   * @param {Object} newSupplierPayments - Supplier payment data
   * @param {Object} newMonthlySpendingPayments - Monthly spending data
   * @param {number} newSaleAmount - Total sales amount
   * @param {number} newSpendingPayments - Other spending payments
   * @param {number} newBankPercent - Bank fees
   * @param {number} moneyDifferenceAmount - Cashbox difference amount
   * @returns {Object} Calculated overall statistics with properties:
   *   {
   *     currentPeriodPayments: number - Sum of all payments made in current period,
   *     currentPeriodSalePayments: number - Sales amount minus non-paid complex sales amount,
   *     currentBalance: number - Difference between sales and current period payments,
   *     upcomingPayments: number - Payments still to be made for current period,
   *     upcomingAccountsReceivable: number - Outstanding balances from complex sales,
   *     upcomingBalanceCurrentPeriod: number - Balance after accounting for upcoming payments,
   *     previousPeriodPayments: number - Sum of all payments for previous periods,
   *     futurePeriodPayments: number - Sum of all payments for future periods,
   *     upcomingBalanceWithPreviousPeriodPayments: number - Final projected balance 
   *   }
   */
  const calculateOverallStats = (newEmployeeSalaries, newSupplierPayments, newMonthlySpendingPayments, newSaleAmount, newSpendingPayments, newBankPercent, moneyDifferenceAmount, complexSales) => {

    // Calculate the non-paid complex sales amount
    let upcomingAccountsReceivable = complexSales?.totalBalance || 0
    let upcomingAccountsReceivableCurrentPeriod = complexSales?.totalBalanceCurrentPeriod || 0

    // Calculate current period sales payments (sales minus accounts receivable)
    let currentPeriodSalePayments = newSaleAmount - upcomingAccountsReceivableCurrentPeriod
    let totalSalePayments = currentPeriodSalePayments + complexSales?.totalPaidPreviousPeriod
    
    let currentPeriodPayments = 
      newEmployeeSalaries?.totalCreditCurrentPeriod + 
      newSupplierPayments?.totalCreditCurrentPeriod + 
      newMonthlySpendingPayments?.totalCreditCurrentPeriod + 
      newSpendingPayments + 
      newBankPercent + 
      moneyDifferenceAmount
      
    let currentBalance = currentPeriodSalePayments - currentPeriodPayments
    
    // ATL-1589: settled per payee, never netted across payees — an overpaid recurring spending
    // used to cancel salaries still owed (see upcomingPayments.ts).
    let upcomingPayments = calculateUpcomingPayments(newEmployeeSalaries, newSupplierPayments, newMonthlySpendingPayments)
      
    let upcomingBalanceCurrentPeriod = currentBalance - upcomingPayments + upcomingAccountsReceivable
    
    let previousPeriodPayments = 
      (newEmployeeSalaries?.totalCredit + newSupplierPayments?.totalCredit + newMonthlySpendingPayments?.totalCredit) - 
      (newEmployeeSalaries?.totalCreditCurrentPeriod + newSupplierPayments?.totalCreditCurrentPeriod + newMonthlySpendingPayments?.totalCreditCurrentPeriod) -
      (newEmployeeSalaries?.totalCreditFuturePeriod + newSupplierPayments?.totalCreditFuturePeriod + newMonthlySpendingPayments?.totalCreditFuturePeriod)

    let futurePeriodPayments =
      (newEmployeeSalaries?.totalCreditFuturePeriod || 0) +
      (newSupplierPayments?.totalCreditFuturePeriod || 0) +
      (newMonthlySpendingPayments?.totalCreditFuturePeriod || 0)
      
    let upcomingBalanceWithPreviousPeriodPayments = upcomingBalanceCurrentPeriod - previousPeriodPayments - futurePeriodPayments
    
    return {
      currentPeriodPayments,
      currentBalance,
      upcomingPayments,
      upcomingBalanceCurrentPeriod,
      previousPeriodPayments,
      futurePeriodPayments,
      upcomingBalanceWithPreviousPeriodPayments,
      currentPeriodSalePayments,
      totalSalePayments,
      upcomingAccountsReceivable,
    }
  }

  /**
   * Set up transaction listener and process transactions
   * This function:
   * 1. Sets up a Firebase listener for expense transactions
   * 2. Resets credit values before processing new transactions
   * 3. Processes each transaction by type
   * 4. Updates totals for each payment category
   * 5. Calculates overall statistics
   * 6. Updates component state with all calculated values
   * 
   * @param {Object} range - Date range with startDate and endDate
   * @param {string} store - Store ID or 'all' for all stores
   * @param {Object} newEmployeeSalaries - Employee salary data
   * @param {Object} newSupplierPayments - Supplier payment data
   * @param {Object} newMonthlySpendingPayments - Monthly spending data
   * @param {number} newSaleAmount - Total sales amount
   * @param {number} newBankPercent - Bank fees
   * @param {number} moneyDifferenceAmount - Cashbox difference amount
   */
  const setupTransactionListener = async (range, store, newEmployeeSalaries, newSupplierPayments, newMonthlySpendingPayments, newSaleAmount, newBankPercent, moneyDifferenceAmount, complexSales, generation) => {
    const endHour = new Date(range.endDate.setHours(23,59,59,999))
    
    // Define base query parameters
    const baseQuery = context.db.collection('transactions').where('type', '==', 'expense')
    const storeQuery = store !== 'all' ? baseQuery.where('store', '==', store) : baseQuery
    
    // ATL-1990 — a failure here propagates to `getDebitCredit`, which shows it; it used to be
    // logged and swallowed, leaving the figures unset (rendered as «0 ₸»).
    // Query 1: First get transactions with paymentRange overlapping current range
    // Since we can't directly query for range overlap in Firestore, we'll get transactions 
    // where paymentRange.endDate is after or equal to range.startDate
    const paymentRangeQuery = storeQuery
      .where('paymentRange.endDate', '>=', range.startDate)
      .orderBy('paymentRange.endDate', 'asc');
    
    // Process payment range transactions
    const paymentRangeTransactions = new Map();
    const paymentRangeQuerySnapshot = await paymentRangeQuery.get();
    if (generation !== loadGenerationRef.current) return
    
    // Add transactions from paymentRange query, filter ones that actually overlap with our range
    paymentRangeQuerySnapshot.forEach(doc => {
      const snap = doc.data();
      
      // Check if this transaction's payment range overlaps with our selected range
      const paymentRangeStartDate = snap.paymentRange?.startDate ? 
        new Date(snap.paymentRange.startDate.toDate()) : 
        new Date(0); // Far past date if no start date
        
      // Only add if the paymentRange overlaps with our selected range
      // (paymentRange.startDate <= range.endDate) && (paymentRange.endDate >= range.startDate)
      // The second part is already guaranteed by our query
      if (paymentRangeStartDate <= endHour) {
        snap.id = doc.id;
        snap.ref = doc.ref;
        paymentRangeTransactions.set(doc.id, snap);
      }
    });
    
    // Query 2: Set up listener for transactions with date in range. Its unsubscribe is kept, so a
    // newer load or leaving the screen stops it — a stale period's listener must not overwrite
    // the figures of the period now selected.
    unsubscribeTransactionsRef.current = storeQuery
      .where('date', '>=', range.startDate)
      .where('date', '<=', endHour)
      .orderBy('date', 'desc')
      .onSnapshot(dateQuerySnapshot => {
        if (generation !== loadGenerationRef.current) return
        try {
          // Reset credit values before processing transactions
          resetCreditValues(newEmployeeSalaries, newSupplierPayments, newMonthlySpendingPayments)
        
          const transactions = new Map();
        
          // First, copy all paymentRange transactions to our Map
          for (const [id, transaction] of paymentRangeTransactions) {
            transactions.set(id, transaction);
          }
        
          // Add transactions from date query (will overwrite any duplicates from paymentRange)
          dateQuerySnapshot.forEach(doc => {
            const snap = doc.data();
            snap.id = doc.id;
            snap.ref = doc.ref;
            transactions.set(doc.id, snap);
          });
        
          // Process all unique transactions
          let paymentTransactions = Array.from(transactions.values());
        
          let newSpendingPayments = 0
          let employeeSalariesTotalCredit = 0
          let employeeSalariesTotalCreditCurrentPeriod = 0
          let employeeSalariesTotalCreditFuturePeriod = 0
          let supplierPaymentsTotalCredit = 0
          let supplierPaymentsTotalCreditCurrentPeriod = 0
          let supplierPaymentsTotalCreditFuturePeriod = 0
          let monthlySpendingPaymentsTotalCredit = 0
          let monthlySpendingPaymentsTotalCreditCurrentPeriod = 0
          let monthlySpendingPaymentsTotalCreditFuturePeriod = 0
        
          // Process each transaction
          for (let t of paymentTransactions) {
            const result = processTransaction(t, newEmployeeSalaries, newSupplierPayments, newMonthlySpendingPayments, range, store)
          
            if (result.employeeSalaries) {
              newEmployeeSalaries = result.employeeSalaries
              employeeSalariesTotalCredit += result.totalCredit
              employeeSalariesTotalCreditCurrentPeriod += result.totalCreditCurrentPeriod
              employeeSalariesTotalCreditFuturePeriod += (result.totalCreditFuturePeriod || 0)
            } else if (result.supplierPayments) {
              newSupplierPayments = result.supplierPayments
              supplierPaymentsTotalCredit += result.totalCredit
              supplierPaymentsTotalCreditCurrentPeriod += result.totalCreditCurrentPeriod
              supplierPaymentsTotalCreditFuturePeriod += (result.totalCreditFuturePeriod || 0)
            } else if (result.monthlySpendingPayments) {
              newMonthlySpendingPayments = result.monthlySpendingPayments
              monthlySpendingPaymentsTotalCredit += result.totalCredit
              monthlySpendingPaymentsTotalCreditCurrentPeriod += result.totalCreditCurrentPeriod
              monthlySpendingPaymentsTotalCreditFuturePeriod += (result.totalCreditFuturePeriod || 0)
            } else {
              newSpendingPayments += result.spendingPayments
            }
          }
        
          // Update total credits
          newEmployeeSalaries.totalCredit = employeeSalariesTotalCredit
          newEmployeeSalaries.totalCreditCurrentPeriod = employeeSalariesTotalCreditCurrentPeriod
          newEmployeeSalaries.totalCreditFuturePeriod = employeeSalariesTotalCreditFuturePeriod
        
          newSupplierPayments.totalCredit = supplierPaymentsTotalCredit
          newSupplierPayments.totalCreditCurrentPeriod = supplierPaymentsTotalCreditCurrentPeriod
          newSupplierPayments.totalCreditFuturePeriod = supplierPaymentsTotalCreditFuturePeriod
        
          newMonthlySpendingPayments.totalCredit = monthlySpendingPaymentsTotalCredit
          newMonthlySpendingPayments.totalCreditCurrentPeriod = monthlySpendingPaymentsTotalCreditCurrentPeriod
          newMonthlySpendingPayments.totalCreditFuturePeriod = monthlySpendingPaymentsTotalCreditFuturePeriod
        
          // Calculate overall statistics
          const newOverallStats = calculateOverallStats(
            newEmployeeSalaries,
            newSupplierPayments,
            newMonthlySpendingPayments,
            newSaleAmount,
            newSpendingPayments,
            newBankPercent,
            moneyDifferenceAmount,
            complexSales
          )
        
          // Update state
          setEmployeeSalaries(newEmployeeSalaries)
          setSupplierPayments(newSupplierPayments)
          setMonthlySpendingPayments(newMonthlySpendingPayments)
          setSaleAmount(newSaleAmount)
          setSpendingPayments(newSpendingPayments)
          setBankPercent(newBankPercent)
          setCashboxShiftDifferece(moneyDifferenceAmount)
          setOverallStats(newOverallStats)
          setLoadState('ready')
        } catch (error) {
          failLoad(generation, error, 'transactions-snapshot')
        }
      }, error => {
        failLoad(generation, error, 'transactions-listener')
      });
  }

  /**
   * Reset credit values for all payment types
   * Prepares objects for new transaction processing by zeroing out credit counters
   * @param {Object} newEmployeeSalaries - Employee salary data
   * @param {Object} newSupplierPayments - Supplier payment data
   * @param {Object} newMonthlySpendingPayments - Monthly spending data
   */
  const resetCreditValues = (newEmployeeSalaries, newSupplierPayments, newMonthlySpendingPayments) => {
    // Reset employee salaries
    for (var key in newEmployeeSalaries) {
      if (key !== 'totalCredit' && key !== 'totalDebit' && key !== 'totalCreditCurrentPeriod' && key !== 'totalCreditFuturePeriod') {
        newEmployeeSalaries[key].totalCredit = 0
        newEmployeeSalaries[key].totalCreditCurrentPeriod = 0
        newEmployeeSalaries[key].totalCreditFuturePeriod = 0
        newEmployeeSalaries[key].salaryTransactions = []
      }
    }
    newEmployeeSalaries.totalCredit = 0
    newEmployeeSalaries.totalCreditCurrentPeriod = 0
    newEmployeeSalaries.totalCreditFuturePeriod = 0
    
    // Reset supplier payments
    for (var key in newSupplierPayments) {
      if (key !== 'totalCredit' && key !== 'totalDebit' && key !== 'totalCreditCurrentPeriod' && key !== 'totalCreditFuturePeriod') {
        newSupplierPayments[key].totalCredit = 0
        newSupplierPayments[key].totalCreditCurrentPeriod = 0
        newSupplierPayments[key].totalCreditFuturePeriod = 0
        newSupplierPayments[key].transactions = []
      }
    }
    newSupplierPayments.totalCredit = 0
    newSupplierPayments.totalCreditCurrentPeriod = 0
    newSupplierPayments.totalCreditFuturePeriod = 0
    
    // Reset monthly spending payments
    for (var key in newMonthlySpendingPayments) {
      if (key !== 'totalCredit' && key !== 'totalDebit' && key !== 'totalCreditCurrentPeriod' && key !== 'totalCreditFuturePeriod') {
        newMonthlySpendingPayments[key].totalCredit = 0
        newMonthlySpendingPayments[key].totalCreditCurrentPeriod = 0
        newMonthlySpendingPayments[key].totalCreditFuturePeriod = 0
        newMonthlySpendingPayments[key].transactions = []
      }
    }
    newMonthlySpendingPayments.totalCredit = 0
    newMonthlySpendingPayments.totalCreditCurrentPeriod = 0
    newMonthlySpendingPayments.totalCreditFuturePeriod = 0
  }

  const getMonthlySpending = (key) => {
    for (let mS of constants.recurringSpendings.data) {
      if (mS.name === key) {
        return {...mS, text: mS.name}
      }
    }
  }

  // Calculate recurring spending amount based on actual days in each month
  const calculateRecurringSpendingAmount = (rS, dateRange) => {
    // Get start and end dates from range
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // If the recurring spending hasn't started yet or has ended before the range
    if (rS.date.toDate() > endDate || (rS.endDate && rS.endDate.toDate() < startDate)) {
      return 0;
    }

    // Adjust range start if recurring spending starts after range start
    if (rS.date.toDate() > startDate) {
      startDate.setTime(rS.date.toDate().getTime());
    }

    // Adjust range end if recurring spending ends before range end
    if (rS.endDate && rS.endDate.toDate() < endDate) {
      endDate.setTime(rS.endDate.toDate().getTime());
    }

    // If dates are in the same month
    if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
      return rS.amount;
    }

    // Calculate number of months between dates
    const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                  (endDate.getMonth() - startDate.getMonth()) + 1;
    
    return rS.amount * months;
  }

  /**
   * Fetch both active and non-active complex sales for the current period
   * Processes their total amounts, payments, and calculates balances
   * Shows only sales with non-zero balance in the list view
   * @param {Object} range - Date range with startDate and endDate
   * @param {string} store - Store ID or 'all' for all stores
   */
  const fetchComplexSales = async (range, store) => {
    // ATL-1990 — no catch: a failure is not "no complex sales". It propagates to `getDebitCredit`,
    // which shows the load as failed; substituting zeros here fed `calculateOverallStats` a
    // receivable of 0 that was never measured.
    // First, get active complex sales
    let newRange = {
      startDate: new Date(range.startDate),
      endDate: new Date(range.startDate)
    }
    let beforeCurrentRange = {
      startDate: new Date(newRange.startDate.setFullYear(newRange.startDate.getFullYear() - 1)),
      endDate: new Date(range.startDate)
    }
    beforeCurrentRange.endDate.setHours(23, 59, 59, 99)
    const activeSalesData = await getComplexSalesOnDateRange(context.db, beforeCurrentRange, true, store, null, true)
    
    // Then, get non-active complex sales from the current period
    const nonActiveSalesData = await getComplexSalesOnDateRange(context.db, range, true, store, null, false)
    
    // Combine both results and remove duplicates by id
    const salesData = [...new Map([...(activeSalesData || []), ...(nonActiveSalesData || [])].map(item => [item.id, item])).values()]
    
    const currentPeriodTransactions = await getComplexSaleTransactionsOnDateRange(range, true, store)
    
    let unpaidSales = []
    let totalBalance = 0
    let totalAmountCurrentPeriod = 0
    let totalPaidCurrentPeriod = 0
    let totalPaidPreviousPeriod = 0
    let totalBalanceCurrentPeriod = 0
    
    // Process each sale
    salesData.forEach(sale => {
      const amount = sale.finalPrice || 0
      let paid = 0
      
      // Calculate paid amount from the payments array inside the complexSale document
      if (sale.payments && Array.isArray(sale.payments)) {
        paid = sale.payments.reduce((total, payment) => {
          return total + (payment.amount || 0)
        }, 0)
      }
      
      const balance = Math.max(0, amount - paid)
      
      // Check if sale was created in current period
      const createdDate = sale.createdDate?.toDate ? sale.createdDate.toDate() : new Date(sale.createdDate)
      const isCreatedInCurrentPeriod = createdDate >= range.startDate && createdDate <= range.endDate
      
      // Add to current period totals if created in current period
      if (isCreatedInCurrentPeriod) {
        totalAmountCurrentPeriod += amount
        totalBalanceCurrentPeriod += balance
      }
      
      // Add sales with balance to the list
      if (balance > 0.01) { // Using a small threshold to account for floating point issues
        unpaidSales.push({
          ...sale,
          amount,
          paid,
          balance,
          isCreatedInCurrentPeriod
        })
      }
      
      totalBalance += balance
    })
    
    // Calculate totalPaidCurrentPeriod and totalPaidPreviousPeriod from currentPeriodTransactions
    let currentPeriodComplexSaleTransactions = []
    let previousPeriodComplexSaleTransactions = []
    if (currentPeriodTransactions && Array.isArray(currentPeriodTransactions)) {
      currentPeriodTransactions.forEach(transaction => {
        const linkedSale = salesData.find(s => s.id === transaction.linkedDocId);
        if (linkedSale) {
          const createdDate = linkedSale.createdDate?.toDate ? linkedSale.createdDate.toDate() : new Date(linkedSale.createdDate);
          const isCreatedInCurrentPeriod = createdDate >= range.startDate && createdDate <= range.endDate;
          if (isCreatedInCurrentPeriod) {
            totalPaidCurrentPeriod += (transaction.value || 0);
            currentPeriodComplexSaleTransactions.push(transaction);
          } else {
            totalPaidPreviousPeriod += (transaction.value || 0);
            previousPeriodComplexSaleTransactions.push(transaction);
          }
        } else {
          totalPaidPreviousPeriod += (transaction.value || 0);
          previousPeriodComplexSaleTransactions.push(transaction);
        }
      });
    }
    
    // Sort sales by creation date, newest first
    unpaidSales.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt)
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt)
      return dateB - dateA
    })

    const complexSalesData = {
      totalPaid: totalPaidCurrentPeriod + totalPaidPreviousPeriod,
      totalBalance,
      totalAmountCurrentPeriod,
      totalPaidCurrentPeriod,
      totalPaidPreviousPeriod,
      sales: unpaidSales,
      totalBalanceCurrentPeriod,
      currentPeriodComplexSaleTransactions,
      previousPeriodComplexSaleTransactions
    }
    
    return complexSalesData
  }

  /**
   * Updates a complex sale item in the local state after it's been edited
   * Recalculates totals based on the updated values
   * @param {Object} updatedItem - The updated complex sale data
   */
  const editComplexSaleItemInData = (updatedItem, sectionIndex, itemIndex) => {
    if (!complexSales || !complexSales.sales || !updatedItem || !updatedItem.id) {
      return
    }

    // Calculate values for the updated item
    const amount = updatedItem.finalPrice || 0
    let paid = 0
    
    if (updatedItem.payments && Array.isArray(updatedItem.payments)) {
      paid = updatedItem.payments.reduce((total, payment) => {
        return total + (payment.amount || 0)
      }, 0)
    }
    
    const balance = amount - paid
    
    // Only include if balance is positive (outstanding amount)
    if (balance <= 0.01) {
      // Remove the item if balance is zero
      const newSales = [...complexSales.sales]
      newSales.splice(itemIndex, 1)
      
      // Recalculate totals
      const newTotalAmount = complexSales.totalAmount - complexSales.sales[itemIndex].amount + amount
      const newTotalPaid = complexSales.totalPaid - complexSales.sales[itemIndex].paid + paid
      const newBalance = newTotalAmount - newTotalPaid
      
      setComplexSales({
        totalAmount: newTotalAmount,
        totalPaid: newTotalPaid,
        balance: newBalance,
        sales: newSales
      })
    } else {
      // Update the item with new values
      const updatedSale = {
        ...updatedItem,
        amount,
        paid,
        balance
      }
      
      const newSales = [...complexSales.sales]
      newSales[itemIndex] = updatedSale
      
      // Recalculate totals
      const newTotalAmount = complexSales.totalAmount - complexSales.sales[itemIndex].amount + amount
      const newTotalPaid = complexSales.totalPaid - complexSales.sales[itemIndex].paid + paid
      const newBalance = newTotalAmount - newTotalPaid
      
      setComplexSales({
        totalAmount: newTotalAmount,
        totalPaid: newTotalPaid,
        balance: newBalance,
        sales: newSales
      })
    }
  }

  /**
   * Renders a list of transactions with dates and amounts
   * @param {Array} transactions - Array of transaction objects
   * @returns {JSX.Element} Transaction list component
   */
  const renderTransactionsList = (transactions) => {
    if (!transactions || transactions.length === 0) return null;
    
    return (
      <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
        <Text>{t('payments.transactions')}: </Text>
        {transactions.map((transaction, idx) => (
          <React.Fragment key={transaction.id}>
            <Text 
              style={{color: colors.primary}} 
              onPress={() => navigation.navigate('PaymentTransactionDetail', {detail: transaction})}
            >
              {format(transaction.date.toDate(), 'd/M/y') + ': ' + currencyFormatter.format(transaction.value)}
            </Text>
            {idx < transactions.length - 1 && <Text>, </Text>}
          </React.Fragment>
        ))}
      </View>
    );
  };

  /**
   * Renders a list of complex sale transactions with dates, amounts, and linked sale names
   * @param {Array} transactions - Array of complex sale transaction objects
   * @returns {JSX.Element} Complex sale transaction list component
   */
  const renderComplexSaleTransactionsList = (transactions, title) => {
    if (!transactions || transactions.length === 0) return null;

    return (
      <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
        <Text>{title}</Text>
        {transactions.map((transaction, idx) => {
          return (
            <React.Fragment key={transaction.id || idx}>
              <Text
                style={{color: colors.primary}}
                onPress={() => {
                  navigation.navigate('ComplexSaleDetail', { id: transaction.linkedDocId } )
                }}
              >
                {/* ATL-1024: these transactions come from `getComplexSaleTransactionsOnDateRange`,
                    which branches to the API for a cut-over org and returns `date` as an ISO
                    STRING. `.toDate()` exists only on a Firestore Timestamp, so this threw and took
                    the receivables panel down. `toNestedDate` (ATL-916) decodes every encoding this
                    field arrives in and returns null rather than inventing a date. */}
                {(() => { const d = toNestedDate(transaction.date); return d ? format(d, 'd/M/y') : '—' })()}: {currencyFormatter.format(transaction.value)}
              </Text>
              {idx < transactions.length - 1 && <Text>, </Text>}
            </React.Fragment>
          );
        })}
      </View>
    );
  };

  /**
   * Renders a list of transactions with dates and amounts
   * @param {Array} unpaidSupplies - Array of unpaid supply objects
   * @returns {JSX.Element} Transaction list component
   */
  const renderUnpaidSupplies = (unpaidSupplies) => {
    if (!unpaidSupplies || unpaidSupplies.length === 0) return null;
    
    return (
      <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
        <Text>{t('payments.unpaidSupplies')}: </Text>
        {unpaidSupplies.map((unpaidSupply, idx) => (
          <React.Fragment key={unpaidSupply.id}>
            <Text 
              style={{color: colors.primary}} 
              onPress={() => navigation.navigate('SupplyDetail', {
                detail: unpaidSupply,
                editItemInData: (updatedItem, sectionIndex, itemIndex) => {
                  if (updatedItem.isPaid) {
                    unpaidSupplies.splice(itemIndex, 1)
                  } else {
                    // ATL-1503: keep the cached copy current — the next payment update's
                    // precondition is read from it.
                    unpaidSupplies.splice(itemIndex, 1, updatedItem)
                  }
                },
                sectionIndex: 0,
                itemIndex: idx
              })}
            >
              {format(unpaidSupply.supplyDate.toDate(), 'd/M/y') + ': ' + currencyFormatter.format(unpaidSupply.totalSum)}
            </Text>
            {idx < unpaidSupplies.length - 1 && <Text>, </Text>}
          </React.Fragment>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.topContainer}>
      <View>
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='top'
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          range={range}
          ref={_rangePicker}
          dateService={localeDateService}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
              void getDebitCredit(newRange, store)
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
              void getDebitCredit({startDate: newRange.startDate, endDate}, store)
            } else {
              setRange(newRange)
            }
          }}
        />
        <StoreChooserHorizontal store={store} style={{marginTop: 10}} onChange={(newStore) => {
          if (range.startDate && range.endDate) {
            setStore(newStore)
            void getDebitCredit(range, newStore)
          }
        }} />
      </View>
      {/* ATL-1990 — figures are rendered only for a load that completed. A failed load is shown as
          a failure with a retry; it must never fall through to the rows below, which render an
          unset value as «0 ₸». */}
      {loadState === 'error' ? (
        <View testID='payment-stats-load-error' style={[styles.loadError, {backgroundColor: theme['background-basic-color-1']}]}>
          <Text status='danger' style={styles.loadErrorText}>{t('payments.loadError')}</Text>
          <Button testID='payment-stats-retry' onPress={retryLoad}>{t('payments.loadRetry')}</Button>
        </View>
      ) : loadState !== 'ready' ? (
        <View testID='payment-stats-loading' style={styles.spinner}><Spinner /></View>
      ) : (
      <ScrollView style={{backgroundColor: theme['background-basic-color-1'], padding: 20, marginTop: 10, borderRadius: 4}}>
        <Text category='h3' style={{ marginBottom: 10 }}>{t('payments.overview')}</Text>
        <InfoRow labelKey='payments.saleAmount' value={saleAmount} t={t} currencyFormatter={currencyFormatter} />
        <InfoRow labelKey='payments.totalSalePayments' value={overallStats?.totalSalePayments} t={t} currencyFormatter={currencyFormatter} />
        <InfoRow labelKey='payments.currentPeriodSalePayments' value={overallStats?.currentPeriodSalePayments} t={t} currencyFormatter={currencyFormatter} />
        <InfoRow labelKey='payments.currentPeriodPayments' value={overallStats?.currentPeriodPayments} t={t} currencyFormatter={currencyFormatter} />
        <InfoRow labelKey='payments.currentBalance' value={overallStats?.currentBalance} t={t} currencyFormatter={currencyFormatter} isBold />
        <InfoRow labelKey='payments.upcomingAccountsReceivable' value={overallStats?.upcomingAccountsReceivable} t={t} currencyFormatter={currencyFormatter} />
        <InfoRow labelKey='payments.upcomingPayments' value={overallStats?.upcomingPayments} t={t} currencyFormatter={currencyFormatter} />
        <InfoRow labelKey='payments.upcomingBalanceCurrentPeriod' value={overallStats?.upcomingBalanceCurrentPeriod} t={t} currencyFormatter={currencyFormatter} isBold />
        <InfoRow labelKey='payments.previousPeriodPayments' value={overallStats?.previousPeriodPayments} t={t} currencyFormatter={currencyFormatter} />
        <InfoRow labelKey='payments.futurePeriodPayments' value={overallStats?.futurePeriodPayments} t={t} currencyFormatter={currencyFormatter} />
        <InfoRow labelKey='payments.upcomingBalanceWithPreviousPeriodPayments' value={overallStats?.upcomingBalanceWithPreviousPeriodPayments} t={t} currencyFormatter={currencyFormatter} isBold />

        <Text category='h3' style={{marginTop: 20}}>{t('payments.salaries')}</Text>
        <Text style={{marginVertical: 5}}>{t('payments.allTimeBalance') + currencyFormatter.format(employeeSalaries?.allTimeDebit - employeeSalaries?.allTimeCredit)}</Text>
        <Text category='label'>{t('payments.currentPeriod')}</Text>
        <Text>{t('payments.totalDebit') + currencyFormatter.format(employeeSalaries?.totalDebit)}</Text>
        <Text>{t('payments.totalCredit') + currencyFormatter.format(employeeSalaries?.totalCredit)}</Text>
        <Text>{t('payments.totalCreditPreviousPeriod') + currencyFormatter.format(employeeSalaries?.totalCredit - employeeSalaries?.totalCreditCurrentPeriod - (employeeSalaries?.totalCreditFuturePeriod || 0))}</Text>
        <Text>{t('payments.totalCreditCurrentPeriod') + currencyFormatter.format(employeeSalaries?.totalCreditCurrentPeriod)}</Text>
        <Text>{t('payments.totalCreditFuturePeriod') + currencyFormatter.format(employeeSalaries?.totalCreditFuturePeriod || 0)}</Text>
        <Text>{t('payments.balance') + currencyFormatter.format(employeeSalaries?.totalCreditCurrentPeriod - employeeSalaries?.totalDebit)}</Text>
        { employeeSalaries ? Object.entries(employeeSalaries).map(([key, value]) => (
          value.totalDebit != null && 
          <View key={key}>
            <View style={[styles.label, {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap'}]}>
              <Text category='s1' style={{color: colors.primary}} onPress={() => {navigation.navigate('Salary', {preselectedUser: key})}}>
                {usersDict[key]?.name}
                {inactiveUsers[key] && <Text>{inactiveUsers[key].name + ' ' + t('deleted')}</Text>}
              </Text>
              <TouchableOpacity onPress={() => navigation.navigate('PaymentTransactionDetail', {newPaymentDetail: {
                linkedDocType: 'workingHour', externalDestination: usersDict[key] || inactiveUsers[key], value: prefilledBalance(value), initialStore: store, paymentRange: range
              }})}><Text style={{color: colors.primary, marginLeft: 10}}>({t('payments.pay')})</Text></TouchableOpacity>
            </View>
            <Text style={[styles.header, {marginVertical: 5}]}>{t('payments.allTimeBalance') + currencyFormatter.format(usersDict[key]?.salaryPaidAmount - usersDict[key]?.salaryCreditedAmount)}</Text>
            <Text category='label'>{t('payments.currentPeriod')}</Text>
            <Text>{t('payments.totalDebit') + currencyFormatter.format(value.totalDebit)}</Text>
            <Text>{t('payments.totalCredit') + currencyFormatter.format(value.totalCredit)}</Text>
            <Text>{t('payments.totalCreditPreviousPeriod') + currencyFormatter.format(value.totalCredit - value.totalCreditCurrentPeriod - (value.totalCreditFuturePeriod || 0))}</Text>
            <Text>{t('payments.totalCreditCurrentPeriod') + currencyFormatter.format(value.totalCreditCurrentPeriod)}</Text>
            <Text>{t('payments.totalCreditFuturePeriod') + currencyFormatter.format(value.totalCreditFuturePeriod || 0)}</Text>
            <Text>{t('payments.currentPeriodBalance') + currencyFormatter.format(value.totalCreditCurrentPeriod - value.totalDebit)}</Text>
            {renderTransactionsList(value.salaryTransactions)}
          </View>
        )) : <View style={styles.spinner}><Spinner /></View>}
      
        <Text category='h3'style={{marginTop: 20}}>{t('payments.suppliers')}</Text>
        <Text>{t('payments.suppliersTotalDebit') + currencyFormatter.format(supplierPayments?.totalDebit)}</Text>
        <Text>{t('payments.totalCreditCurrentPeriod') + currencyFormatter.format(supplierPayments?.totalCreditCurrentPeriod)}</Text>
        <Text>{t('payments.balance') + currencyFormatter.format((supplierPayments?.totalCreditCurrentPeriod - supplierPayments?.totalDebit))}</Text>
        <Text>{t('payments.totalCreditPreviousPeriod') + currencyFormatter.format((supplierPayments?.totalCredit - supplierPayments?.totalCreditCurrentPeriod - (supplierPayments?.totalCreditFuturePeriod || 0)))}</Text>
        <Text>{t('payments.totalCreditFuturePeriod') + currencyFormatter.format(supplierPayments?.totalCreditFuturePeriod || 0)}</Text>
        <Text>{t('payments.totalCredit') + currencyFormatter.format(supplierPayments?.totalCredit)}</Text>
        <Text>{t('payments.totalUnpaidSupplies') + currencyFormatter.format(supplierPayments?.totalUnpaidSumPreviousPeriod || 0)}</Text>
        { supplierPayments ? Object.entries(supplierPayments).map(([key, value]) => (
          value.totalDebit != null &&
          <View key={key}>
            <View style={[styles.label, {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap'}]}>
              <Text category='s1' style={{color: colors.primary}} onPress={() => {navigation.navigate('SupplierDetail', {detail: suppliersDict[key], preselectedStore: store})}}>{suppliersDict[key]?.name}</Text>
              <TouchableOpacity onPress={() => navigation.navigate('PaymentTransactionDetail', {newPaymentDetail: {
                linkedDocType: 'supply', externalDestination: suppliersDict[key], value: prefilledBalance(value), initialStore: store, paymentRange: range
              }})}><Text style={{color: colors.primary, marginLeft: 10}}>({t('payments.pay')})</Text></TouchableOpacity>
            </View>
            <Text>{t('payments.suppliersTotalDebit') + currencyFormatter.format(value.totalDebit || 0)}</Text>
            <Text>{t('payments.totalCreditCurrentPeriod') + currencyFormatter.format(value.totalCreditCurrentPeriod || 0)}</Text>
            <Text>{t('payments.balance') + currencyFormatter.format(((value.totalCreditCurrentPeriod || 0) - (value.totalDebit || 0)))}</Text>
            <Text>{t('payments.totalCreditPreviousPeriod') + currencyFormatter.format(((value.totalCredit || 0) - (value.totalCreditCurrentPeriod || 0) - ((value.totalCreditFuturePeriod || 0) || 0)))}</Text>
            <Text>{t('payments.totalCreditFuturePeriod') + currencyFormatter.format(value.totalCreditFuturePeriod || 0)}</Text>
            <Text>{t('payments.totalCredit') + currencyFormatter.format(value.totalCredit || 0)}</Text>
            <Text>{t('payments.totalUnpaidSupplies') + currencyFormatter.format(value.totalUnpaidSumPreviousPeriod || 0)}</Text>
            {renderTransactionsList(value.transactions)}
            {renderUnpaidSupplies(value.unpaidSupplies)}
          </View>
        )) : <View style={styles.spinner}><Spinner /></View>}

        <Text category='h3'style={{marginTop: 20}}>{t('payments.monthlySpendings')}</Text>
        <Text>{t('payments.totalDebit') + currencyFormatter.format(monthlySpendingPayments?.totalDebit)}</Text>
        <Text>{t('payments.totalCredit') + currencyFormatter.format(monthlySpendingPayments?.totalCredit)}</Text>
        <Text>{t('payments.totalCreditPreviousPeriod') + currencyFormatter.format((monthlySpendingPayments?.totalCredit - monthlySpendingPayments?.totalCreditCurrentPeriod - (monthlySpendingPayments?.totalCreditFuturePeriod || 0)))}</Text>
        <Text>{t('payments.totalCreditCurrentPeriod') + currencyFormatter.format(monthlySpendingPayments?.totalCreditCurrentPeriod)}</Text>
        <Text>{t('payments.totalCreditFuturePeriod') + currencyFormatter.format(monthlySpendingPayments?.totalCreditFuturePeriod || 0)}</Text>
        <Text>{t('payments.balance') + currencyFormatter.format((monthlySpendingPayments?.totalCreditCurrentPeriod - monthlySpendingPayments?.totalDebit))}</Text>
        { monthlySpendingPayments ? Object.entries(monthlySpendingPayments).map(([key, value]) => (
          key !== 'totalDebit' && key !== 'totalCredit' && key !== 'totalCreditCurrentPeriod' && key !== 'totalCreditFuturePeriod' &&
          <View key={key}>
            <View style={[styles.label, {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap'}]}>
              <Text category='s1'>{key}</Text>
              <TouchableOpacity onPress={() => navigation.navigate('PaymentTransactionDetail', {newPaymentDetail: {
                linkedDocType: 'recurringSpending', externalDestination: getMonthlySpending(key), value: prefilledBalance(value), initialStore: store, paymentRange: range
              }})}><Text style={{color: colors.primary, marginLeft: 10}}>({t('payments.pay')})</Text></TouchableOpacity>
            </View>
            <Text>{t('payments.totalDebit') + currencyFormatter.format(value.totalDebit)}</Text>
            <Text>{t('payments.totalCredit') + currencyFormatter.format(value.totalCredit)}</Text>
            <Text>{t('payments.totalCreditPreviousPeriod') + currencyFormatter.format((value.totalCredit - value.totalCreditCurrentPeriod - (value.totalCreditFuturePeriod || 0)))}</Text>
            <Text>{t('payments.totalCreditCurrentPeriod') + currencyFormatter.format(value.totalCreditCurrentPeriod)}</Text>
            <Text>{t('payments.totalCreditFuturePeriod') + currencyFormatter.format(value.totalCreditFuturePeriod || 0)}</Text>
            <Text>{t('payments.balance') + currencyFormatter.format((value.totalCreditCurrentPeriod - value.totalDebit))}</Text>
            {renderTransactionsList(value.transactions)}
          </View>
        )) : <View style={styles.spinner}><Spinner /></View>}   

        <Text category='h3'style={{marginTop: 20}}>{t('payments.otherPayments')}</Text>
        <Text>{t('payments.spendingPayments') + currencyFormatter.format(spendingPayments)}</Text>
        <Text>{t('payments.bankPercent') + currencyFormatter.format(bankPercent)}</Text>
        <Text>{t('payments.cashboxShiftDifference') + currencyFormatter.format(cashboxShiftDifference)}</Text>

        <Text category='h3' style={{marginTop: 20}}>{t('payments.complexSales')}</Text>
        {complexSales ? (
          <>
            <Text>{t('payments.totalCreditSalesAmountCurrentPeriod') + currencyFormatter.format(complexSales.totalAmountCurrentPeriod)}</Text>
            <Text>{t('payments.totalCreditSalesPaidAmountCurrentPeriod') + currencyFormatter.format(complexSales.totalPaidCurrentPeriod)}</Text>
            <Text>{t('payments.totalCreditSalesPaidAmountPreviousPeriod') + currencyFormatter.format(complexSales.totalPaidPreviousPeriod)}</Text>
            <Text>{t('payments.totalCreditSalesPaidAmount') + currencyFormatter.format(complexSales.totalPaid)}</Text>
            <Text>{t('payments.totalAccountsReceivable') + currencyFormatter.format(complexSales.totalBalance)}</Text>
            {renderComplexSaleTransactionsList(complexSales.currentPeriodComplexSaleTransactions, t('payments.paymentsForCurrentPeriodSales'))}
            {renderComplexSaleTransactionsList(complexSales.previousPeriodComplexSaleTransactions, t('payments.paymentsForPreviousPeriodSales'))}

            {complexSales.sales.length > 0 && (
              <View style={{marginTop: 10}}>
                <Text category='s1'>{t('payments.unpaidComplexSalesList')}</Text>
                <FlatList
                  data={complexSales.sales}
                  keyExtractor={(item) => item.id}
                  renderItem={({item, index}) => (
                    <View>
                      <View style={[styles.label, {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap'}]}>
                        <Text category='s1' style={{color: colors.primary}} onPress={() => navigation.navigate('ComplexSaleDetail', {
                          detail: item,
                          editItemInData: editComplexSaleItemInData,
                          itemIndex: index
                        })}>{item.name + (toNestedDate(item.createdDate) ? ' (' + format(toNestedDate(item.createdDate), 'd/M/y') + ')' : '')}</Text>
                      </View>
                      <Text>{t('payments.creditSalesAmount') + currencyFormatter.format(item.amount)}</Text>
                      <Text>{t('payments.creditSalesPaidAmount') + currencyFormatter.format(item.paid)}</Text>
                      <Text>{t('payments.accountsReceivable') + currencyFormatter.format(item.balance)}</Text>
                    </View>
                  )}
                />
              </View>
            )}
          </>
        ) : (
          <View style={styles.spinner}><Spinner /></View>
        )}

        <ModalView isVisible={foundErrorModalIsShown} setIsVisible={setFoundErrorModalIsShown}>
          <Text style={{marginBottom: 5}} category="h6">{t('stats.foundError')}</Text>
          <Text style={{marginBottom: 5}}>{t('payments.foundErrorDescription')}</Text>
          <PlatformDependantSelect
            data={Object.values(usersDict)}
            selectedOption={selectedUserForRecalculation}
            onSelect={(value) => setSelectedUserForRecalculation(value)}
            style={{marginBottom: 5}}
          />
          { refreshingAllTimeSalaryPaidCredited 
            ? 
              <View style={{alignItems: 'center'}}><Spinner /></View>
            :
              <Button onPress={async () => {
                if (!selectedUserForRecalculation) return;
                setRefreshingAllTimeSalaryPaidCredited(true);
                await recalculateUserSalaryAmounts(selectedUserForRecalculation.uid);
                setRefreshingAllTimeSalaryPaidCredited(false);
              }}>{t('stats.recalculate')}</Button>
          }
        </ModalView>
        <Button appearance="ghost" status='basic' style={{marginTop: 10}} onPress={() => setFoundErrorModalIsShown(true)}>{t('stats.foundError')}</Button>

        <View style={{height: 50}}></View>
      </ScrollView>
      )}
    </View>
    
  )
}

const styles = StyleSheet.create({
  topContainer: {
    flex: 1, 
    padding: 20,
    // alignItems: 'center',
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  loadError: {
    marginTop: 10,
    padding: 20,
    borderRadius: 4,
    alignItems: 'flex-start',
  },
  loadErrorText: {
    marginBottom: 12,
  },
  label: {
    marginTop: 10,
  }
});
